import React, { useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import ReactEcharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { MultisetBarChart as MultisetBarChartType } from '../../../../types/charts';
import { useEChartsTheme, getChartColors, useTheme } from '../../../../theme';
import { chartImageExtractor } from '../../../../helpers/chartImageExtractor';

interface MultisetBarChartProps {
  chartData: MultisetBarChartType;
  width?: number;
  height?: number;
  chartId?: string;
}

const MultisetBarChart: React.FC<MultisetBarChartProps> = ({
  chartData,
  width = 400,
  height = 300,
  chartId
}) => {
  const theme = useTheme();
  const echartsTheme = useEChartsTheme();
  const chartRef = useRef<any>(null);

  const option: EChartsOption = useMemo(() => {
    const { data, config, title, series } = chartData;
    const defaultColors = getChartColors(theme, 'default');

    const categories = data.map((item) => item.name);

    const seriesData = series.map((seriesName, index) => ({
      name: seriesName,
      type: 'bar',
      data: data.map((item) => item.values[seriesName] || 0),
      itemStyle: {
        color: config?.colors?.[index] || defaultColors[index % defaultColors.length]
      },
      label: config?.showValues
        ? {
            show: true,
            position: config?.horizontal ? 'right' : 'top',
            color: echartsTheme.labelColor
          }
        : undefined,
      emphasis: {
        itemStyle: {
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.3)'
        }
      }
    }));

    return {
      title: title
        ? {
            text: title,
            left: 'left',
            textStyle: {
              fontSize: 16,
              fontWeight: 500,
              color: echartsTheme.textColor
            }
          }
        : undefined,
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        backgroundColor: echartsTheme.tooltipBg,
        borderColor: echartsTheme.borderColor,
        borderWidth: 1,
        textStyle: {
          color: echartsTheme.textColor
        },
        confine: true,
        z: 9999
      },
      legend: {
        data: series,
        top: title ? '8%' : '2%',
        textStyle: {
          color: echartsTheme.textColor
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: title ? '18%' : '12%',
        containLabel: true
      },
      xAxis: config?.horizontal
        ? {
            type: 'value',
            axisLine: {
              lineStyle: {
                color: echartsTheme.borderColor
              }
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: echartsTheme.textColor
            }
          }
        : {
            type: 'category',
            data: categories,
            axisLine: {
              lineStyle: {
                color: echartsTheme.borderColor
              }
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: echartsTheme.textColor
            }
          },
      yAxis: config?.horizontal
        ? {
            type: 'category',
            data: categories,
            axisLine: {
              lineStyle: {
                color: echartsTheme.borderColor
              }
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: echartsTheme.textColor
            }
          }
        : {
            type: 'value',
            axisLine: {
              lineStyle: {
                color: echartsTheme.borderColor
              }
            },
            axisTick: {
              show: false
            },
            axisLabel: {
              color: echartsTheme.textColor
            }
          },
      series: seriesData
    };
  }, [chartData, theme, echartsTheme]);

  // Register chart instance for image extraction
  useEffect(() => {
    return () => {
      // Cleanup on unmount
      if (chartId) {
        chartImageExtractor.unregisterChart(chartId);
      }
    };
  }, [chartId]);

  const onChartReady = (chart: any) => {
    if (chartId) {
      chartImageExtractor.registerChart(chartId, chart);
    }
  };

  return (
    <ChartContainer>
      <ReactEcharts
        ref={chartRef}
        option={option}
        style={{ width: `${width}px`, height: `${height}px` }}
        opts={{ renderer: 'canvas' }}
        onChartReady={onChartReady}
      />
    </ChartContainer>
  );
};

export default MultisetBarChart;

const ChartContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;
